import FileUploadResult from './file-upload-result.js';
import UploaderFile from './uploader-file.js';

export interface Config {
  currentFiles: Array<UploaderFile>;
  extensions: Array<string>;
  filesToProcess: Array<File>;
  maxFileCount: number;
  maxFileSizeInBytes: number;
  allowDuplicates: boolean;
}

// key is source file extension and browser infered content type
const mimeTypeMap: { [key: string]: string } = {
  'csv-application/vnd.ms-excel': 'text/csv',
};

function mapBrowserContentTypes(fileExtension: string, file: File) {
  if (mimeTypeMap[`${fileExtension}-${file.type}`]) {
    return new File([file], file.name, {
      type: mimeTypeMap[`${fileExtension}-${file.type}`],
    });
  }
  return file;
}

export const processFiles = (config: Config): Array<FileUploadResult> => {
  const {
    currentFiles,
    extensions,
    filesToProcess,
    maxFileCount,
    maxFileSizeInBytes,
    allowDuplicates,
  } = config;

  const results: Array<FileUploadResult> = [];

  let filesToUploadCount = 0; // counter for how many files we add in the "for" loop below; used to know when we reach the maximum number of files allowed

  for (let file of filesToProcess) {
    // check file extension
    const fileExtension = file.name.split('.').pop() || '';
    const isAllowedExtension = extensions.includes(
      fileExtension.toLocaleLowerCase()
    );
    file = mapBrowserContentTypes(fileExtension, file);
    if (!isAllowedExtension) {
      const message = `File extension for "${file.name}" is not allowed`;
      results.push({ file, message, uploaded: false, id: crypto.randomUUID() });
      continue;
    }

    // check for max file size
    if (file.size > maxFileSizeInBytes) {
      const message = `The file you are attempting to upload exceeds the maximum size allowed.`;
      results.push({ file, message, uploaded: false, id: crypto.randomUUID() });
      continue;
    }

    // check for max file count
    const noMoreFilesAllowed =
      currentFiles.length + filesToUploadCount >= maxFileCount;
    if (noMoreFilesAllowed) {
      const message = `File not uploaded; it would exceed the maximum number of files (${maxFileCount})`;
      results.push({ file, message, uploaded: false, id: crypto.randomUUID() });
      continue;
    }

    // check for duplicate file names
    if (!allowDuplicates) {
      const isDuplicate = currentFiles.some(
        (f: UploaderFile) => f.name === file.name
      );
      if (isDuplicate) {
        const message = `File not uploaded; there is already a file named ${file.name}`;
        results.push({
          file,
          message,
          uploaded: false,
          id: crypto.randomUUID(),
        });
        continue;
      }
    }

    // The file passes all validation and can be added
    results.push({
      file,
      message: 'File uploaded',
      uploaded: true,
      id: crypto.randomUUID(),
    });
    filesToUploadCount += 1;
  }

  return results;
};
